import { getTripLocationsArray } from './tripUtils';
import { capMapZoomToMinRadius, MIN_EXPLORE_RADIUS_MILES, zoomForRadiusMiles } from './googleMapLayout';

const milesBetween = (lat1, lng1, lat2, lng2) => {
  const toRad = (value) => (value * Math.PI) / 180;
  const earthRadiusMiles = 3958.8;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
  return earthRadiusMiles * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
};

const normalizeLocationName = (name = '') => name.trim().toLowerCase();

const getCityToken = (locationName = '') =>
  locationName.split(',')[0].trim().toLowerCase();

const US_STATE_ABBREVS = new Set([
  'al', 'ak', 'az', 'ar', 'ca', 'co', 'ct', 'de', 'fl', 'ga', 'hi', 'id', 'il', 'in', 'ia',
  'ks', 'ky', 'la', 'me', 'md', 'ma', 'mi', 'mn', 'ms', 'mo', 'mt', 'ne', 'nv', 'nh', 'nj',
  'nm', 'ny', 'nc', 'nd', 'oh', 'ok', 'or', 'pa', 'ri', 'sc', 'sd', 'tn', 'tx', 'ut', 'vt',
  'va', 'wa', 'wv', 'wi', 'wy', 'dc',
]);

const escapeRegExp = (value = '') => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/** US-looking names keep the Dublin, CA geocode bias; international cities must not. */
export const isUsLocationName = (name = '') => {
  const normalized = normalizeLocationName(name);
  if (!normalized) return false;
  if (/\b(usa|u\.s\.a?|united states)\b/.test(normalized)) return true;
  const statePart = normalized.split(',')[1]?.trim().split(/\s+/)[0];
  return Boolean(statePart && US_STATE_ABBREVS.has(statePart));
};

export const shouldBiasGeocodeToUs = (location, tripLocations = []) => {
  if (isUsLocationName(location?.name)) return true;
  const named = tripLocations.filter((tripLocation) => tripLocation?.name);
  if (named.length === 0) return false;
  const usCount = named.filter((tripLocation) => isUsLocationName(tripLocation.name)).length;
  return usCount * 2 >= named.length;
};

/** Circle literal expected by Place.searchByText — `{ lat, lng, radius }` is invalid. */
export const buildPlacesLocationBias = (coords, radiusMeters = 25000) => {
  if (coords?.lat == null || coords?.lng == null) {
    return undefined;
  }
  return {
    center: { lat: coords.lat, lng: coords.lng },
    radius: radiusMeters,
  };
};

/** Strip the trip city (and autocomplete "City - Country" suffix) from a search query. */
export const getQueryWithoutLocation = (query = '', location) => {
  let rest = query.trim();
  if (rest.includes(' - ')) {
    rest = rest.split(' - ')[0];
  }
  rest = rest.toLowerCase();
  if (location?.name) {
    const fullName = normalizeLocationName(location.name);
    const city = getCityToken(location.name);
    if (fullName) {
      rest = rest.replace(fullName, ' ');
    }
    if (city.length >= 3) {
      rest = rest.replace(new RegExp(`\\b${escapeRegExp(city)}\\b`, 'gi'), ' ');
    }
  }
  return rest.replace(/[-–,]/g, ' ').replace(/\s+/g, ' ').trim();
};

export const isLocationBrowseQuery = (query, location) => {
  if (!location) return false;
  return getQueryWithoutLocation(query, location) === '';
};

/** Known coordinates for common trip stops (avoids Dublin → Ireland). */
const KNOWN_TRIP_COORDS = {
  'dublin, ca, usa': { lat: 37.7022, lng: -121.9358 },
  'dublin, ca': { lat: 37.7022, lng: -121.9358 },
  'riverside, ca, usa': { lat: 33.9533, lng: -117.3962 },
  'riverside, ca': { lat: 33.9533, lng: -117.3962 },
  'san diego, ca, usa': { lat: 32.7157, lng: -117.1611 },
  'san diego, ca': { lat: 32.7157, lng: -117.1611 },
  'irvine, ca, usa': { lat: 33.6846, lng: -117.8265 },
  'irvine, ca': { lat: 33.6846, lng: -117.8265 },
};

const lookupKnownCoords = (locationName = '') => {
  const normalized = normalizeLocationName(locationName);
  if (KNOWN_TRIP_COORDS[normalized]) {
    return KNOWN_TRIP_COORDS[normalized];
  }
  const cityToken = getCityToken(locationName);
  return KNOWN_TRIP_COORDS[`${cityToken}, ca, usa`] || KNOWN_TRIP_COORDS[`${cityToken}, ca`] || null;
};

/** Unique trip stops for Explore — Dublin at start/end counts once in the list. */
export const getUniqueTripLocationsForExplore = (trip) => {
  const locations = getTripLocationsArray(trip);
  const seen = new Set();

  return locations.filter((location) => {
    const key = normalizeLocationName(location.name);
    if (!key || seen.has(key)) {
      return false;
    }
    seen.add(key);
    return true;
  });
};

/** If the query mentions a trip city (e.g. "halal Riverside"), return that stop. */
export const findTripLocationInQuery = (query, locations = []) => {
  const normalizedQuery = query.trim().toLowerCase();
  if (!normalizedQuery) {
    return null;
  }

  const sorted = [...locations].sort(
    (a, b) => getCityToken(b.name).length - getCityToken(a.name).length
  );

  return (
    sorted.find((location) => {
      const cityToken = getCityToken(location.name);
      const fullName = normalizeLocationName(location.name);
      const cityWithState = fullName.split(',').slice(0, 2).join(',').trim();
      return (
        normalizedQuery.includes(fullName) ||
        (cityWithState.length > cityToken.length && normalizedQuery.includes(cityWithState)) ||
        (cityToken.length >= 3 && normalizedQuery.includes(cityToken))
      );
    }) || null
  );
};

/** Geocode a trip stop. US trips keep a US bias so "Dublin" resolves to Dublin, CA. */
export const geocodeTripLocation = async (location, tripLocations = []) => {
  if (location?.coordinates?.lat != null && location?.coordinates?.lng != null) {
    return location.coordinates;
  }

  const known = lookupKnownCoords(location?.name);
  if (known) {
    return known;
  }

  const resolvedCoords = tripLocations
    .map((tripLocation) => tripLocation.coordinates || lookupKnownCoords(tripLocation.name))
    .filter(Boolean);

  if (window.google?.maps?.Geocoder && location?.name) {
    const geocoder = new window.google.maps.Geocoder();
    const geocodeRequest = {
      address: location.name,
    };
    if (shouldBiasGeocodeToUs(location, tripLocations)) {
      geocodeRequest.componentRestrictions = { country: 'US' };
      geocodeRequest.region = 'us';
    }

    if (resolvedCoords.length >= 2 && window.google.maps.LatLngBounds) {
      const bounds = new window.google.maps.LatLngBounds();
      resolvedCoords.forEach((coord) => bounds.extend(coord));
      geocodeRequest.bounds = bounds;
    }

    const geocoded = await new Promise((resolve) => {
      geocoder.geocode(geocodeRequest, (results, status) => {
        if (status === 'OK' && results?.[0]?.geometry?.location) {
          const point = results[0].geometry.location;
          resolve({ lat: point.lat(), lng: point.lng() });
          return;
        }
        resolve(null);
      });
    });

    if (geocoded) {
      return geocoded;
    }
  }

  return lookupKnownCoords(location?.name);
};

/** Resolve coordinates for all unique trip stops. */
export const resolveAllTripLocationCoords = async (locations = []) => {
  const coordMap = new Map();

  for (const location of locations) {
    const coords = await geocodeTripLocation(location, locations);
    if (coords) {
      coordMap.set(location.name, coords);
    }
  }

  return coordMap;
};

/** Keep only POIs within maxMiles of a trip stop. */
export const filterPlacesNearLocation = (places = [], coords, maxMiles = 50) => {
  if (!coords) {
    return places;
  }

  return places.filter((place) => {
    if (!place.geometry) {
      return false;
    }
    return milesBetween(
      place.geometry.lat,
      place.geometry.lng,
      coords.lat,
      coords.lng
    ) <= maxMiles;
  });
};

/** Which city sections to show in the results list. */
export const getLocationsForResultsList = (locations = [], activeSearchLocation = null) => {
  if (!activeSearchLocation) {
    return locations;
  }
  return locations.filter((location) => location.name === activeSearchLocation);
};

export const centerMapOnCoordinates = (map, coords, zoom = zoomForRadiusMiles(MIN_EXPLORE_RADIUS_MILES)) => {
  if (!map || !coords) {
    return;
  }
  map.setCenter(coords);
  map.setZoom(zoom);
};

const applyBoundsThenCapZoom = (map, bounds, padding) => {
  map.fitBounds(bounds, padding);
  if (window.google?.maps?.event?.addListenerOnce) {
    window.google.maps.event.addListenerOnce(map, 'idle', () => {
      capMapZoomToMinRadius(map, MIN_EXPLORE_RADIUS_MILES);
    });
    return;
  }
  capMapZoomToMinRadius(map, MIN_EXPLORE_RADIUS_MILES);
};

export const fitMapToPlaces = (map, places = [], padding = 48) => {
  if (!map || !window.google?.maps || places.length === 0) {
    return;
  }

  const bounds = new window.google.maps.LatLngBounds();
  places.forEach((place) => {
    if (place.geometry) {
      bounds.extend(place.geometry);
    }
  });

  if (bounds.isEmpty()) {
    return;
  }

  applyBoundsThenCapZoom(map, bounds, padding);
};

export const fitMapToCoordinates = (map, coordinates = [], padding = 48) => {
  if (!map || !window.google?.maps || coordinates.length === 0) {
    return;
  }

  const valid = coordinates.filter((coord) => coord?.lat != null && coord?.lng != null);
  if (valid.length === 0) {
    return;
  }

  if (valid.length === 1) {
    centerMapOnCoordinates(map, valid[0], zoomForRadiusMiles(MIN_EXPLORE_RADIUS_MILES));
    return;
  }

  const bounds = new window.google.maps.LatLngBounds();
  valid.forEach((coord) => bounds.extend(coord));

  if (!bounds.isEmpty()) {
    applyBoundsThenCapZoom(map, bounds, padding);
  }
};

/** Assign a place to the closest trip stop (or match by address city name). */
export const matchPlaceToTripLocation = (place, locations = [], coordMap = new Map()) => {
  if (place.searchLocation) {
    return place.searchLocation;
  }

  const address = (place.address || '').toLowerCase();
  if (address) {
    const addressMatch = locations.find((location) => {
      const cityToken = getCityToken(location.name);
      return cityToken.length >= 3 && address.includes(cityToken);
    });
    if (addressMatch) {
      return addressMatch.name;
    }
  }

  if (!place.geometry) {
    return locations[0]?.name || 'Unknown';
  }

  let closestName = locations[0]?.name || 'Unknown';
  let closestDistance = Infinity;

  locations.forEach((location) => {
    const coords = coordMap.get(location.name) || location.coordinates;
    if (!coords) {
      return;
    }
    const distance = milesBetween(
      place.geometry.lat,
      place.geometry.lng,
      coords.lat,
      coords.lng
    );
    if (distance < closestDistance) {
      closestDistance = distance;
      closestName = location.name;
    }
  });

  return closestDistance <= 75 ? closestName : closestName;
};

export { milesBetween, getCityToken, lookupKnownCoords, KNOWN_TRIP_COORDS };
